import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { ApiError } from './api-error';

export type ApiSurface = 'admin' | 'platform';

export interface HttpHooks {
  /** Called once per 401. The app redirects to login unless the request opted out. */
  onUnauthorized?: (error: ApiError) => void;
  /** Called for every mapped error, for monitoring breadcrumbs. */
  onError?: (error: ApiError, request: InternalAxiosRequestConfig) => void;
}

export interface HttpOptions {
  baseURL: string;
  surface: ApiSurface;
  timeoutMs?: number;
  hooks?: HttpHooks;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Set on login/session probes so a 401 does not trigger the global redirect. */
    skipUnauthorizedHook?: boolean;
  }
}

/**
 * Sent on every request. The backend refuses cookie-authenticated requests without it, which is
 * its CSRF check; the value itself is informational.
 */
export const CLIENT_HEADER = 'X-Nucleus-Client';
/** Response header carrying the backend's request id; the same id is inside every error body. */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * Creates the axios instance every generated request function goes through.
 * Cookie sessions: credentials are always sent, nothing is stored in JS. Login also returns an
 * access token for API clients; the browser apps ignore it and rely on the cookie.
 */
export function createHttp({
  baseURL,
  surface,
  timeoutMs = 30_000,
  hooks = {},
}: HttpOptions): AxiosInstance {
  const http = axios.create({
    baseURL,
    timeout: timeoutMs,
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      [CLIENT_HEADER]: `${surface}-web`,
    },
  });

  http.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      const apiError = ApiError.from(error);
      const request = axios.isAxiosError(error) ? error.config : undefined;
      if (request) hooks.onError?.(apiError, request);
      if (apiError.kind === 'unauthorized' && !request?.skipUnauthorizedHook) {
        hooks.onUnauthorized?.(apiError);
      }
      return Promise.reject(apiError);
    },
  );

  return http;
}

let current: AxiosInstance | undefined;

/** Installs the instance the generated clients use. Call once at app boot. */
export function configureApiClient(options: HttpOptions): AxiosInstance {
  current = createHttp(options);
  return current;
}

/** For tests: inject a prepared instance instead of building one. */
export function setHttp(instance: AxiosInstance | undefined): void {
  current = instance;
}

export function getHttp(): AxiosInstance {
  if (!current) {
    throw new Error('API client not configured. Call configureApiClient() at boot.');
  }
  return current;
}

export type RequestOptions = AxiosRequestConfig;
